import type { PropertyDescriptor, SourceContext, SourceTypeDefinition } from '../core/definitions';
import { str } from './base';
import { StreamRuntime } from './streamRuntime';

/**
 * `guestId` holds the participant id of a personal invitation when the picker had one: that
 * stays the same across the guest's reloads, new tabs and later broadcasts of the room. A plain
 * per-connection guest id (older projects, shared-link guests) still works while it lasts.
 */
export type RemoteGuestSettings = {
  guestId: string;
  /** The guest's audio stays in the program mix in every scene, like an OBS global audio device (SRC-D-015). */
  alwaysOnProgram?: boolean;
};

class RemoteGuestRuntime extends StreamRuntime<RemoteGuestSettings> {
  private guestId: string;
  private stream: MediaStream | null = null;
  private signature = '';
  private readonly unsubscribe: () => void;
  private readonly onTracksChanged = () => this.sync();

  constructor(ctx: SourceContext, settings: Partial<RemoteGuestSettings>) {
    super(ctx);
    this.guestId = str(settings.guestId, '');
    this.unsubscribe = ctx.guests.subscribe(() => this.sync());
    this.sync();
  }

  update(settings: RemoteGuestSettings): void {
    if (this.destroyed) return;
    const next = str(settings.guestId, '');
    if (next === this.guestId) return;
    this.guestId = next;
    this.sync();
  }

  private sync(): void {
    if (this.destroyed) return;
    const stream = this.guestId ? this.ctx.guests.getStream(this.guestId) : null;
    const signature = stream ? `${stream.id}:${stream.getTracks().map((t) => t.id).join(',')}` : '';
    if (stream === this.stream && signature === this.signature) return;

    this.stream?.removeEventListener('addtrack', this.onTracksChanged);
    this.stream?.removeEventListener('removetrack', this.onTracksChanged);
    this.stream = stream;
    this.signature = signature;
    const epoch = this.beginEpoch();

    if (!stream) {
      this.clearOutput();
      this.setStatus('idle');
      return;
    }
    stream.addEventListener('addtrack', this.onTracksChanged);
    stream.addEventListener('removetrack', this.onTracksChanged);
    this.setStatus('starting');
    this.present(stream, epoch, { video: true, audio: true, activateAudioElement: true, waitForVideo: false }).then(
      (ok) => {
        if (ok) this.setStatus('live');
      },
      (err) => {
        if (this.isCurrent(epoch)) this.fail('unknown', err instanceof Error ? err.message : String(err));
      },
    );
  }

  protected release(): void {
    this.unsubscribe();
    this.stream?.removeEventListener('addtrack', this.onTracksChanged);
    this.stream?.removeEventListener('removetrack', this.onTracksChanged);
    this.stream = null;
    // The tracks belong to the guest connection; only the local element and node are released.
    this.clearOutput();
  }
}

export const remoteGuestDefinition: SourceTypeDefinition<RemoteGuestSettings> = {
  kind: 'remote-guest',
  displayName: 'Remote Guest',
  caps: { video: true, audio: true },
  category: 'network',
  // One guest is one input: a copy would add the same voice to the mix a second time.
  duplicate: 'reference',
  defaults: () => ({ guestId: '', alwaysOnProgram: false }),
  properties(): PropertyDescriptor[] {
    return [
      { key: 'guestId', label: 'Guest', type: 'guest' },
      {
        key: 'alwaysOnProgram',
        label: 'Audio always on program',
        type: 'boolean',
        description: 'Keeps this guest audible in every scene, even scenes without the guest item; the picture still shows only where the item is placed.',
      },
    ];
  },
  create: (ctx, settings) => new RemoteGuestRuntime(ctx, settings),
};
